import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { loadMaterialUsageForSession } from "../../helpers/projectMaterialLedger";
import { loadUsersByAuthIdsForOrg } from "../../helpers/scopedQueries";
import { usePermissions } from "../../hooks/usePermissions";

const USAGE_ROW_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "16px",
  marginBottom: "12px",
};

function formatCurrency(amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  return `$${safe.toFixed(2)}`;
}

function formatAddedAt(timestamp) {
  if (!timestamp) return null;

  try {
    return new Date(timestamp).toLocaleString("en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

/**
 * Read-only materials list for the Active Session manager drill-down.
 * Reuses project material usage loading and Project Summary card styling.
 */
export function ActiveSessionMaterialsSection({
  sessionId,
  showPricing = false,
  refreshKey = 0,
}) {
  const { actor } = usePermissions();
  const [rows, setRows] = useState([]);
  const [employeeNames, setEmployeeNames] = useState({});
  const [loading, setLoading] = useState(false);

  const loadMaterials = useCallback(async () => {
    if (!sessionId) {
      setRows([]);
      setEmployeeNames({});
      return;
    }

    setLoading(true);

    try {
      const usageRows = await loadMaterialUsageForSession(sessionId, { actor });
      setRows(usageRows);

      const enteredByIds = [
        ...new Set(usageRows.map((row) => row.entered_by).filter(Boolean)),
      ];

      if (enteredByIds.length === 0 || !actor) {
        setEmployeeNames({});
        return;
      }

      const users = await loadUsersByAuthIdsForOrg(
        supabase,
        actor,
        enteredByIds,
        "supabase_user_id, employee_name",
      );

      const nameMap = {};
      users.forEach((user) => {
        nameMap[user.supabase_user_id] = user.employee_name;
      });
      setEmployeeNames(nameMap);
    } catch (error) {
      console.error("ACTIVE SESSION MATERIALS LOAD ERROR:", error);
      setRows([]);
      setEmployeeNames({});
    } finally {
      setLoading(false);
    }
  }, [sessionId, actor]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials, refreshKey]);

  useEffect(() => {
    if (!sessionId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadMaterials();
    }, 15000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [sessionId, loadMaterials]);

  return (
    <div style={{ marginTop: "8px", marginBottom: "20px" }}>
      <div className="section-heading" style={{ marginBottom: "16px" }}>
        MATERIALS USED
      </div>

      {loading ? (
        <p className="kpi-empty kpi-empty--pad">Loading materials…</p>
      ) : rows.length === 0 ? (
        <p className="kpi-empty kpi-empty--pad">
          No materials have been logged for this session.
        </p>
      ) : (
        <div className="kpi-task-list">
          {rows.map((row) => {
            const addedAt = formatAddedAt(row.created_at);
            const addedByName = row.entered_by
              ? employeeNames[row.entered_by] || null
              : null;

            return (
              <div key={row.usage_id} style={USAGE_ROW_STYLE}>
                <p className="kpi-task-title" style={{ margin: "0 0 12px" }}>
                  {row.material_name_snapshot}
                </p>

                <div className="section-heading">QUANTITY</div>
                <div style={{ marginTop: "-8px", marginBottom: "12px" }}>
                  {Number(row.quantity)}
                </div>

                <div className="section-heading">UNIT OF MEASURE</div>
                <div style={{ marginTop: "-8px", marginBottom: "12px" }}>
                  {row.unit_of_measure_snapshot || "units"}
                </div>

                {addedAt && (
                  <>
                    <div className="section-heading">DATE/TIME ADDED</div>
                    <div style={{ marginTop: "-8px", marginBottom: "12px" }}>
                      {addedAt}
                    </div>
                  </>
                )}

                {addedByName && (
                  <>
                    <div className="section-heading">ADDED BY</div>
                    <div style={{ marginTop: "-8px", marginBottom: "12px" }}>
                      {addedByName}
                    </div>
                  </>
                )}

                {showPricing && (
                  <>
                    <div className="section-heading">UNIT COST</div>
                    <div style={{ marginTop: "-8px", marginBottom: "12px" }}>
                      {formatCurrency(row.unit_cost_snapshot)}
                    </div>

                    <div className="section-heading">EXTENDED COST</div>
                    <div style={{ marginTop: "-8px", marginBottom: row.notes ? "12px" : 0 }}>
                      {formatCurrency(row.total_cost)}
                    </div>
                  </>
                )}

                {row.notes ? (
                  <div
                    style={{
                      opacity: 0.65,
                      fontSize: "12px",
                      marginTop: "8px",
                    }}
                  >
                    {row.notes}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
